import { useEffect, useMemo, useState } from 'react';
import {
  Settings as SettingsIcon,
  Brain,
  MessageCircle,
  Mail,
  Globe,
  Save,
  TestTube,
  RefreshCw,
  Bot,
  Zap,
  Key,
  Copy,
  Eye,
  EyeOff,
  Calendar,
  BookOpen,
  Inbox,
  Users,
  Clock,
  Database,
  FileText,
  ScanSearch,
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { DocumentsSettingsSection } from './DocumentsSettingsSection';
import { GmailEmailSettingsSection } from './GmailEmailSettingsSection';
import { MessagePatternsSettingsSection } from './MessagePatternsSettingsSection';
import { DEFAULT_AI_MODEL, SUGGESTED_AI_MODELS } from '@/config/aiDefaults';

interface BaseSettings {
  ai: {
    model: string;
    temperature: number;
    maxTokens: number;
    prompt: string;
    whatsappEnabled: boolean;
    emailEnabled: boolean;
    documentsEnabled: boolean;
    autoReply: boolean;
    businessHoursEnabled: boolean;
    businessHoursStart: string;
    businessHoursEnd: string;
    businessHoursTimezone: string;
    maxContextMessages: number;
  };
  aiTimeouts: {
    whatsapp: number;
    email: number;
    documents: number;
    general: number;
    calendar: number;
    practices: number;
  };
  email: {
    provider: 'gmail' | 'outlook' | 'custom' | 'local';
    host?: string;
    port?: number;
    username?: string;
    password?: string;
    secure: boolean;
  };
  general: {
    companyName: string;
    timezone: string;
    language: string;
  };
}

interface WhatsAppStatus {
  configured: boolean;
  transport?: string;
  engine?: string;
  session?: any;
  aiEnabled: boolean;
  autoReply: boolean;
  model: string | null;
  businessHours: {
    enabled: boolean;
    start: string;
    end: string;
    timezone: string;
  } | null;
  webhookToken: string;
  webhookUrl: string;
  openwaWebhookUrl?: string;
}

interface WhatsAppConfigForm {
  transport: 'META' | 'OPENWA';
  accessToken: string;
  phoneNumberId: string;
  webhookVerifyToken: string;
  businessAccountId: string;
  appId: string;
  appSecret: string;
  openwaBaseUrl: string;
  openwaSessionId: string;
  openwaApiKey: string;
  openwaApiKeyConfigured: boolean;
  aiEnabled: boolean;
  aiModel: string;
  autoReply: boolean;
  businessHoursEnabled: boolean;
  businessHoursStart: string;
  businessHoursEnd: string;
  businessHoursTimezone: string;
  aiPrompt: string;
  aiDisclosureMessage: string;
  maxContextMessages: number;
  morningDigestEnabled: boolean;
  morningDigestHour: string;
  engine: string;
}

type SettingsSection =
  | 'overview'
  | 'general'
  | 'ai-core'
  | 'whatsapp'
  | 'email'
  | 'clients'
  | 'practices'
  | 'calendar'
  | 'documents'
  | 'ai-timeouts'
  | 'parsing'
  | 'backup-schedules';

const DEFAULT_WHATSAPP_CONFIG: WhatsAppConfigForm = {
  transport: 'META',
  accessToken: '',
  phoneNumberId: '',
  webhookVerifyToken: '',
  businessAccountId: '',
  appId: '',
  appSecret: '',
  openwaBaseUrl: 'http://openwa:2785',
  openwaSessionId: '',
  openwaApiKey: '',
  openwaApiKeyConfigured: false,
  aiEnabled: false,
  aiModel: DEFAULT_AI_MODEL,
  autoReply: false,
  businessHoursEnabled: false,
  businessHoursStart: '09:00',
  businessHoursEnd: '18:00',
  businessHoursTimezone: 'Europe/Rome',
  aiPrompt:
    "Sei l'assistente AI di Studio Gori, studio tecnico di geometri. Rispondi in modo professionale, conciso e in italiano.",
  aiDisclosureMessage:
    "Gentile cliente, questa chat è gestita dall'assistente automatico di Studio Gori, un sistema di intelligenza artificiale. Le risposte possono essere generate senza intervento umano. Per parlare con un operatore dello studio, scriva operatore.",
  maxContextMessages: 5,
  morningDigestEnabled: true,
  morningDigestHour: '08:00',
  engine: 'whatsapp-web.js',
};

const SettingsPage = () => {
  const [activeSection, setActiveSection] = useState<SettingsSection>('overview');
  const [baseSettings, setBaseSettings] = useState<BaseSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const [whatsappStatus, setWhatsappStatus] = useState<WhatsAppStatus | null>(null);
  const [whatsappConfig, setWhatsappConfig] = useState<WhatsAppConfigForm>(DEFAULT_WHATSAPP_CONFIG);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [showSecrets, setShowSecrets] = useState({ accessToken: false, appSecret: false });
  const [testResults, setTestResults] = useState<{ connection?: string; ai?: string }>({});
  const [aiLoading, setAiLoading] = useState(false);
  const [pullingModel, setPullingModel] = useState<string | null>(null);

  useEffect(() => {
    void loadBaseSettings();
    void loadWhatsAppData();
    void loadAIModels();
    const section = new URLSearchParams(window.location.search).get('section');
    if (section === 'email') {
      setActiveSection('email');
    }
    if (section === 'parsing') {
      setActiveSection('parsing');
    }
  }, []);

  const withToken = useMemo(
    () => (init?: RequestInit) => {
      const token = localStorage.getItem('accessToken');
      return {
        ...init,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(init?.headers || {}),
        },
      } satisfies RequestInit;
    },
    []
  );

  async function loadBaseSettings() {
    try {
      setSettingsLoading(true);
      const response = await fetch('/api/settings', withToken());
      const data = await response.json();
      if (data.success) {
        setBaseSettings(data.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Errore nel recupero delle impostazioni.');
    } finally {
      setSettingsLoading(false);
    }
  }

  async function loadWhatsAppData() {
    try {
      setWhatsappLoading(true);
      const [statusRes, configRes, modelsRes] = await Promise.all([
        fetch('/api/whatsapp/status', withToken()),
        fetch('/api/whatsapp/config', withToken()),
        fetch('/api/whatsapp/models', withToken()),
      ]);

      const statusJson = await statusRes.json();
      if (statusJson.success) {
        setWhatsappStatus(statusJson.data);
      }

      const configJson = await configRes.json();
      if (configJson.success) {
        const safe = configJson.data || {};
        setWhatsappConfig((prev) => ({
          ...prev,
          transport: safe.transport || prev.transport,
          phoneNumberId: safe.phoneNumberId || '',
          businessAccountId: safe.businessAccountId || '',
          appId: safe.appId || '',
          webhookVerifyToken: safe.webhookVerifyToken || prev.webhookVerifyToken,
          openwaBaseUrl: safe.openwaBaseUrl || prev.openwaBaseUrl,
          openwaSessionId: safe.openwaSessionId || '',
          openwaApiKeyConfigured: Boolean(safe.openwaApiKeyConfigured),
          engine: safe.engine || prev.engine,
          aiEnabled: safe.aiEnabled ?? prev.aiEnabled,
          aiModel: safe.aiModel || prev.aiModel,
          autoReply: safe.autoReply ?? prev.autoReply,
          businessHoursEnabled: safe.businessHours?.enabled ?? prev.businessHoursEnabled,
          businessHoursStart: safe.businessHours?.start || prev.businessHoursStart,
          businessHoursEnd: safe.businessHours?.end || prev.businessHoursEnd,
          businessHoursTimezone:
            safe.businessHours?.timezone || prev.businessHoursTimezone,
          aiPrompt: safe.aiPrompt || prev.aiPrompt,
          aiDisclosureMessage:
            typeof safe.aiDisclosureMessage === 'string'
              ? safe.aiDisclosureMessage
              : prev.aiDisclosureMessage,
          maxContextMessages: safe.maxContextMessages || prev.maxContextMessages,
          morningDigestEnabled: safe.morningDigestEnabled ?? prev.morningDigestEnabled,
          morningDigestHour:
            typeof safe.morningDigestHour === 'string' && /^\d{1,2}:\d{2}$/.test(safe.morningDigestHour)
              ? `${String(Number(safe.morningDigestHour.split(':')[0])).padStart(2, '0')}:${safe.morningDigestHour.split(':')[1]}`
              : prev.morningDigestHour,
        }));
      }

      const modelsJson = await modelsRes.json();
      if (modelsJson.success) {
        setAvailableModels(modelsJson.data || []);
      }
    } catch (error) {
      console.error('Error fetching WhatsApp data:', error);
      toast.error('Errore nel recupero delle impostazioni WhatsApp.');
    } finally {
      setWhatsappLoading(false);
    }
  }

  async function loadAIModels() {
    try {
      const response = await fetch('/api/ai/models', withToken());
      const data = await response.json();
      if (data.success) {
        setAvailableModels(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching AI models:', error);
      // Fallback models
      setAvailableModels([DEFAULT_AI_MODEL]);
    }
  }

  async function pullAIModel(modelName: string) {
    try {
      setPullingModel(modelName);
      toast.message(`Download di ${modelName} in corso. Può richiedere molti minuti: non chiudere la pagina.`);
      const response = await fetch('/api/ai/pull-model', withToken({
        method: 'POST',
        body: JSON.stringify({ modelName }),
        signal: AbortSignal.timeout(45 * 60 * 1000),
      }));
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        await loadAIModels();
      } else {
        toast.error(data.message || 'Errore nel download del modello');
      }
    } catch (error) {
      console.error('Error pulling model:', error);
      toast.error('Errore nel download del modello');
    } finally {
      setPullingModel(null);
    }
  }

  async function handleSave(section: string, payload: any) {
    setSavingKey(section);
    try {
      const response = await fetch(`/api/settings/${section}`, withToken({
        method: 'PUT',
        body: JSON.stringify(payload),
      }));
      const data = await response.json();
      if (data.success) {
        toast.success('Impostazioni salvate!');
        await loadBaseSettings();
        // Invalida cache settings per forzare refresh in tutti i componenti
        localStorage.setItem('settings:invalidate', Date.now().toString());
        // Trigger un evento custom per forzare refresh immediato
        console.log('Dispatching settings:changed event...');
        window.dispatchEvent(new Event('settings:changed'));
      } else {
        toast.error(data.message || 'Errore nel salvataggio.');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Errore nel salvataggio delle impostazioni.');
    } finally {
      setSavingKey(null);
    }
  }

  async function handleSaveAITimeouts(payload: BaseSettings['aiTimeouts']) {
    setSavingKey('ai-timeouts');
    try {
      const response = await fetch('/api/settings/ai-timeouts', withToken({
        method: 'PUT',
        body: JSON.stringify(payload),
      }));
      const data = await response.json();
      if (data.success) {
        toast.success('Timeout AI aggiornati!');
        await loadBaseSettings();
      } else {
        toast.error(data.message || 'Errore nel salvataggio timeout.');
      }
    } catch (error) {
      console.error('Error saving AI timeouts:', error);
      toast.error('Errore nel salvataggio dei timeout AI.');
    } finally {
      setSavingKey(null);
    }
  }

  async function saveWhatsAppConfig(partial?: Partial<WhatsAppConfigForm>) {
    setWhatsappLoading(true);
    try {
      const merged = { ...whatsappConfig, ...(partial || {}) };
      const payload: Record<string, unknown> = {
        accessToken: merged.accessToken,
        phoneNumberId: merged.phoneNumberId,
        webhookVerifyToken: merged.webhookVerifyToken,
        businessAccountId: merged.businessAccountId,
        appId: merged.appId,
        appSecret: merged.appSecret,
        openwaBaseUrl: merged.openwaBaseUrl,
        openwaSessionId: merged.openwaSessionId,
        aiEnabled: merged.aiEnabled,
        aiModel: merged.aiModel,
        aiPrompt: merged.aiPrompt,
        aiDisclosureMessage: merged.aiDisclosureMessage,
        autoReply: merged.autoReply,
        businessHoursEnabled: merged.businessHoursEnabled,
        businessHoursStart: merged.businessHoursStart,
        businessHoursEnd: merged.businessHoursEnd,
        businessHoursTimezone: merged.businessHoursTimezone,
        maxContextMessages: merged.maxContextMessages,
        morningDigestEnabled: merged.morningDigestEnabled,
        morningDigestHour: merged.morningDigestHour,
      };
      if (merged.openwaApiKey?.trim()) {
        payload.openwaApiKey = merged.openwaApiKey.trim();
      }
      const response = await fetch('/api/whatsapp/config', withToken({
        method: 'POST',
        body: JSON.stringify(payload),
      }));
      const data = await response.json();
      if (data.success) {
        toast.success('Configurazione WhatsApp aggiornata!');
        setWhatsappConfig((prev) => ({
          ...prev,
          ...merged,
          openwaApiKey: '',
          openwaApiKeyConfigured: Boolean(prev.openwaApiKeyConfigured || merged.openwaApiKey?.trim()),
        }));
        await loadWhatsAppData();
      } else {
        toast.error(data.message || 'Errore durante il salvataggio della configurazione WhatsApp.');
      }
    } catch (error) {
      console.error('Error updating WhatsApp config:', error);
      toast.error('Errore durante il salvataggio della configurazione WhatsApp.');
    } finally {
      setWhatsappLoading(false);
    }
  }

  async function testWhatsappConnection() {
    setWhatsappLoading(true);
    try {
      const response = await fetch('/api/whatsapp/test-connection', withToken({ method: 'POST' }));
      const data = await response.json();
      if (data.success) {
        toast.success(data.data?.message || 'Test connessione riuscito!');
        setTestResults((prev) => ({ ...prev, connection: data.data?.message }));
      } else {
        toast.error(data.message || 'Test connessione fallito');
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      toast.error('Errore durante il test della connessione');
    } finally {
      setWhatsappLoading(false);
    }
  }

  async function testWhatsappAI() {
    setWhatsappLoading(true);
    try {
      const response = await fetch('/api/whatsapp/test-ai', withToken({ method: 'POST' }));
      const data = await response.json();
      if (data.success) {
        toast.success(data.data?.message || 'Test AI riuscito!');
        setTestResults((prev) => ({ ...prev, ai: data.data?.message }));
      } else {
        toast.error(data.message || 'Test AI fallito');
      }
    } catch (error) {
      console.error('Error testing AI:', error);
      toast.error('Errore durante il test AI');
    } finally {
      setWhatsappLoading(false);
    }
  }

  async function generateWebhookToken() {
    setWhatsappLoading(true);
    try {
      const response = await fetch('/api/whatsapp/generate-token', withToken({ method: 'POST' }));
      const data = await response.json();
      if (data.success) {
        setWhatsappConfig((prev) => ({
          ...prev,
          webhookVerifyToken: data.data?.token || prev.webhookVerifyToken,
        }));
        toast.success('Nuovo webhook token generato!');
      } else {
        toast.error(data.message || 'Errore nella generazione del token');
      }
    } catch (error) {
      console.error('Error generating token:', error);
      toast.error('Errore nella generazione del token');
    } finally {
      setWhatsappLoading(false);
    }
  }

  async function pullModel(modelName: string) {
    if (!modelName) return;
    setWhatsappLoading(true);
    try {
      toast.message(`Download di ${modelName} in corso. Può richiedere molti minuti: non chiudere la pagina.`);
      const response = await fetch('/api/ai/pull-model', withToken({
        method: 'POST',
        body: JSON.stringify({ modelName }),
        signal: AbortSignal.timeout(45 * 60 * 1000),
      }));
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Modello scaricato con successo!');
        await loadAIModels();
      } else {
        toast.error(data.message || 'Errore nel download del modello');
      }
    } catch (error) {
      console.error('Error pulling model:', error);
      toast.error('Errore nel download del modello');
    } finally {
      setWhatsappLoading(false);
    }
  }

  const sections: Array<{
    id: SettingsSection;
    label: string;
    icon: React.ReactNode;
    description: string;
  }> = useMemo(
    () => [
      {
        id: 'overview',
        label: 'Panoramica',
        icon: <SettingsIcon className="h-4 w-4" />,
        description: 'Riepilogo impostazioni principali',
      },
      {
        id: 'general',
        label: 'Generali',
        icon: <Globe className="h-4 w-4" />,
        description: 'Nome studio, fuso orario, lingua',
      },
      {
        id: 'ai-core',
        label: 'AI Core',
        icon: <Brain className="h-4 w-4" />,
        description: 'Impostazioni modelli AI globali',
      },
      {
        id: 'whatsapp',
        label: 'WhatsApp',
        icon: <MessageCircle className="h-4 w-4" />,
        description: 'Configurazione WhatsApp Business e AI',
      },
      {
        id: 'email',
        label: 'Email',
        icon: <Mail className="h-4 w-4" />,
        description: 'SMTP/IMAP e provider email',
      },
      {
        id: 'clients',
        label: 'Clienti',
        icon: <Users className="h-4 w-4" />,
        description: 'Parametri gestione clienti (presto)',
      },
      {
        id: 'practices',
        label: 'Pratiche',
        icon: <BookOpen className="h-4 w-4" />,
        description: 'Workflow pratiche e checklist (presto)',
      },
      {
        id: 'calendar',
        label: 'Calendario',
        icon: <Calendar className="h-4 w-4" />,
        description: 'Sincronizzazioni agenda (presto)',
      },
      {
        id: 'documents',
        label: 'Documenti',
        icon: <Inbox className="h-4 w-4" />,
        description: 'NAS Volume_1, URL app, manutenzione',
      },
      {
        id: 'ai-timeouts',
        label: 'AI Timeouts',
        icon: <Clock className="h-4 w-4" />,
        description: 'Gestisci timeout per ogni servizio AI',
      },
      {
        id: 'parsing',
        label: 'Parsing',
        icon: <ScanSearch className="h-4 w-4" />,
        description: 'Casistiche WhatsApp/AI live',
      },
      {
        id: 'backup-schedules',
        label: 'Backup Automatici',
        icon: <Database className="h-4 w-4" />,
        description: 'Schedule backup database e retention policy',
      },
    ],
    []
  );

  if (settingsLoading && !baseSettings) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg">Caricamento impostazioni...</div>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0">
      <div className="-mx-4 border-b bg-white sm:-mx-5 lg:-mx-6">
        <div className="flex flex-col gap-4 px-4 py-6 sm:px-5 lg:flex-row lg:items-center lg:justify-between lg:px-6">
          <div>
            <div className="flex items-center gap-3">
              <SettingsIcon className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Impostazioni di {baseSettings?.general?.companyName || 'Studio Gori'}</h1>
                <p className="text-sm text-gray-500">
                  Configura tutte le integrazioni e i moduli del gestionale
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {sections.map((section) => (
              <Button
                key={section.id}
                variant={activeSection === section.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveSection(section.id)}
              >
                <span className="mr-2 flex items-center gap-2">
                  {section.icon}
                  <span>{section.label}</span>
                </span>
              </Button>
            ))}
          </div>
        </div>
        </div>

      <main className="w-full min-w-0 space-y-8 py-8">
        {activeSection === 'overview' && (
          <Card>
            <CardHeader>
              <CardTitle>Panoramica Rapida</CardTitle>
              <CardDescription>Stato dei principali moduli del sistema.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <StatusTile
                title="WhatsApp Business"
                status={whatsappStatus?.configured ? 'OK' : 'Configura'}
                description={
                  whatsappStatus?.configured
                    ? 'Integrazione attiva'
                    : 'Completa la configurazione'
                }
              />
              <StatusTile
                title="AI Risposte"
                status={whatsappStatus?.aiEnabled ? 'Attiva' : 'Spenta'}
                description={
                  whatsappStatus?.aiEnabled
                    ? `Modello: ${whatsappStatus.model}`
                    : 'Ai disattivata'
                }
              />
              <StatusTile
                title="Email"
                status={(baseSettings?.email.username && baseSettings.email.password) ? 'OK' : 'Configura'}
                description="SMTP/IMAP"
              />
            </CardContent>
          </Card>
        )}

        {activeSection === 'general' && baseSettings && (
          <GeneralSettingsSection
            settings={baseSettings}
            savingKey={savingKey}
            onSave={(payload) => handleSave('general', payload)}
          />
        )}

        {activeSection === 'ai-core' && baseSettings && (
          <AICoreSettingsSection
            settings={baseSettings}
            models={availableModels}
            savingKey={savingKey}
            onSave={(payload) => handleSave('ai', payload)}
            onPullModel={pullAIModel}
            pullingModel={pullingModel}
          />
        )}

        {activeSection === 'whatsapp' && (
          <WhatsAppSettingsSection
            config={whatsappConfig}
            status={whatsappStatus}
            models={availableModels}
            loading={whatsappLoading}
            showSecrets={showSecrets}
            setShowSecrets={setShowSecrets}
            testResults={testResults}
            onConfigChange={setWhatsappConfig}
            onSave={saveWhatsAppConfig}
            onRefresh={loadWhatsAppData}
            onTestConnection={testWhatsappConnection}
            onTestAI={testWhatsappAI}
            onGenerateToken={generateWebhookToken}
            onPullModel={pullModel}
          />
        )}

        {activeSection === 'email' && <GmailEmailSettingsSection />}

        {activeSection === 'clients' && <PlaceholderCard icon={<Users className="h-8 w-8" />} title="Impostazioni Clienti" />} 
        {activeSection === 'practices' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-6 w-6" />
                Impostazioni Pratiche
              </CardTitle>
              <CardDescription>
                Gestisci i tipi di pratiche disponibili
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Tipi Pratiche</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Configura e gestisci i tipi di pratiche personalizzati
                    </p>
                  </div>
                  <Button onClick={() => window.location.href = '/practice-types'}>
                    <FileText className="h-4 w-4 mr-2" />
                    Gestisci Tipi Pratiche
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )} 
        {activeSection === 'calendar' && <PlaceholderCard icon={<Calendar className="h-8 w-8" />} title="Calendario e Agenda" />} 
        {activeSection === 'documents' && (
          <DocumentsSettingsSection savingKey={savingKey} setSavingKey={setSavingKey} />
        )} 
        {activeSection === 'ai-timeouts' && baseSettings && (
          <AITimeoutsSection
            settings={baseSettings}
            savingKey={savingKey}
            onSave={handleSaveAITimeouts}
          />
        )}

        {activeSection === 'parsing' && <MessagePatternsSettingsSection />}

        {activeSection === 'backup-schedules' && (
          <BackupSchedulesSection />
        )}
      </main>
            </div>
  );

  async function handleTestEmail() {
    try {
      setSavingKey('email:test');
      const response = await fetch('/api/settings/test-connection', withToken({
        method: 'POST',
        body: JSON.stringify({ type: 'email' }),
      }));
      const data = await response.json();
      if (data.success) {
        toast.success('Test email completato con successo!');
      } else {
        toast.error(data.message || 'Errore nel test email');
      }
    } catch (error) {
      console.error('Error testing email:', error);
      toast.error('Errore nel test email');
    } finally {
      setSavingKey(null);
    }
  }
};

export default SettingsPage;

// ---------------------------------------------
// Helper Components
// ---------------------------------------------

function StatusTile({
  title,
  status,
  description,
}: {
  title: string;
  status: string;
  description: string;
}) {
  return (
    <div className="border rounded-lg bg-white p-4 shadow-sm">
      <div className="text-xs uppercase text-gray-400">{title}</div>
      <div className="text-lg font-semibold text-gray-900">{status}</div>
      <div className="text-xs text-gray-500">{description}</div>
    </div>
  );
}

function GeneralSettingsSection({
  settings,
  savingKey,
  onSave,
}: {
  settings: BaseSettings;
  savingKey: string | null;
  onSave: (payload: BaseSettings['general']) => Promise<void>;
}) {
  const [form, setForm] = useState(settings.general);

  useEffect(() => {
    setForm(settings.general);
  }, [settings.general]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Impostazioni Generali</CardTitle>
        <CardDescription>Informazioni dello studio e preferenze principali.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-3">
        <div className="space-y-2">
          <Label>Nome Studio</Label>
          <Input
            value={form.companyName}
            onChange={(e) => setForm((prev) => ({ ...prev, companyName: e.target.value }))}
            placeholder="Studio Gori"
          />
        </div>
        <div className="space-y-2">
          <Label>Fuso Orario</Label>
          <Input
            value={form.timezone}
            onChange={(e) => setForm((prev) => ({ ...prev, timezone: e.target.value }))}
            placeholder="Europe/Rome"
          />
        </div>
        <div className="space-y-2">
          <Label>Lingua</Label>
          <Select
            value={form.language}
            onValueChange={(value) => setForm((prev) => ({ ...prev, language: value }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="it">Italiano</SelectItem>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="fr">Française</SelectItem>
              <SelectItem value="de">Deutsch</SelectItem>
              <SelectItem value="es">Español</SelectItem>
            </SelectContent>
          </Select>
              </div>
      </CardContent>
      <CardContent>
        <Button
          onClick={() => onSave(form)}
          disabled={savingKey === 'general'}
          className="bg-gray-800 text-white"
        >
          <Save className="h-4 w-4 mr-2" />
          {savingKey === 'general' ? 'Salvando...' : 'Salva'}
        </Button>
      </CardContent>
    </Card>
  );
}

function AICoreSettingsSection({
  settings,
  models,
  savingKey,
  onSave,
  onPullModel,
  pullingModel,
}: {
  settings: BaseSettings;
  models: string[];
  savingKey: string | null;
  onSave: (payload: BaseSettings['ai']) => Promise<void>;
  onPullModel: (modelName: string) => Promise<void>;
  pullingModel: string | null;
}) {
  const [form, setForm] = useState(settings.ai);
  const [newModelName, setNewModelName] = useState('');

  useEffect(() => {
    setForm(settings.ai);
  }, [settings.ai]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5" />
            Configurazione AI Principale
          </CardTitle>
          <CardDescription>
            Gestisci modelli, parametri e risposte automatiche per tutti i servizi dello studio.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Modello e parametri */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-2">
              <Label>Modello AI</Label>
              <Select value={form.model} onValueChange={(value) => setForm((prev) => ({ ...prev, model: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona modello" />
                </SelectTrigger>
                <SelectContent>
                  {models.map((model) => (
                    <SelectItem key={model} value={model}>
                      {model}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Temperature ({form.temperature.toFixed(1)})</Label>
              <input
                type="range"
                min="0"
                max="2"
                step="0.1"
                value={form.temperature}
                onChange={(e) => setForm((prev) => ({ ...prev, temperature: parseFloat(e.target.value) }))}
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <Label>Max Tokens</Label>
              <Input
                type="number"
                value={form.maxTokens}
                onChange={(e) => setForm((prev) => ({ ...prev, maxTokens: parseInt(e.target.value) || 2048 }))}
              />
            </div>
          </div>

          {/* Prompt personalizzato */}
          <div className="space-y-2">
            <Label>Prompt AI Personalizzato</Label>
            <Textarea
              value={form.prompt}
              onChange={(e) => setForm((prev) => ({ ...prev, prompt: e.target.value }))}
              placeholder="Sei l'assistente AI di Studio Gori..."
              rows={4}
            />
          </div>

          {/* Risposte automatiche per servizi */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-4">
              <h4 className="font-medium">Risposte Automatiche</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <MessageCircle className="h-4 w-4" />
                    WhatsApp
                  </Label>
                  <Switch
                    checked={form.whatsappEnabled}
                    onCheckedChange={(checked) => setForm((prev) => ({ ...prev, whatsappEnabled: checked }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email
                  </Label>
                  <Switch
                    checked={form.emailEnabled}
                    onCheckedChange={(checked) => setForm((prev) => ({ ...prev, emailEnabled: checked }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4" />
                    Documenti
                  </Label>
                  <Switch
                    checked={form.documentsEnabled}
                    onCheckedChange={(checked) => setForm((prev) => ({ ...prev, documentsEnabled: checked }))}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-medium">Orari di Lavoro</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Abilita orari di lavoro</Label>
                  <Switch
                    checked={form.businessHoursEnabled}
                    onCheckedChange={(checked) => setForm((prev) => ({ ...prev, businessHoursEnabled: checked }))}
                  />
                </div>
                {form.businessHoursEnabled && (
                  <>
                    <div className="grid gap-2 grid-cols-2">
                      <div>
                        <Label className="text-xs">Dalle</Label>
                        <Input
                          type="time"
                          value={form.businessHoursStart}
                          onChange={(e) => setForm((prev) => ({ ...prev, businessHoursStart: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Alle</Label>
                        <Input
                          type="time"
                          value={form.businessHoursEnd}
                          onChange={(e) => setForm((prev) => ({ ...prev, businessHoursEnd: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Fuso orario</Label>
                      <Select
                        value={form.businessHoursTimezone}
                        onValueChange={(value) => setForm((prev) => ({ ...prev, businessHoursTimezone: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Europe/Rome">Europa/Roma</SelectItem>
                          <SelectItem value="Europe/London">Europa/Londra</SelectItem>
                          <SelectItem value="America/New_York">America/New York</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <Button
            onClick={() => onSave(form)}
            disabled={savingKey === 'ai'}
            className="w-fit"
          >
            {savingKey === 'ai' ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Salva configurazione AI
          </Button>
        </CardContent>
      </Card>

      {/* Download nuovi modelli */}
      <Card>
        <CardHeader>
          <CardTitle>Gestione Modelli</CardTitle>
          <CardDescription>
            Scarica nuovi modelli AI da Ollama o gestisci quelli esistenti.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Nome modello (es: lfm2.5:8b)"
              value={newModelName}
              onChange={(e) => setNewModelName(e.target.value)}
              className="flex-1"
            />
            <Button
              onClick={() => {
                if (newModelName.trim()) {
                  void onPullModel(newModelName.trim());
                }
              }}
              disabled={!newModelName.trim() || !!pullingModel}
            >
              {pullingModel ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Zap className="h-4 w-4 mr-2" />
              )}
              {pullingModel ? `Scarico ${pullingModel}…` : 'Scarica'}
            </Button>
          </div>
          
          <div className="text-sm text-muted-foreground">
            <p><strong>Modelli disponibili:</strong> {models.join(', ')}</p>
            <p className="mt-1">
              <strong>Suggeriti:</strong> {SUGGESTED_AI_MODELS.join(', ')} (LFM2.5-8B-A1B, thread da OLLAMA_NUM_THREAD)
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function WhatsAppSettingsSection({
  config,
  status,
  models,
  loading,
  showSecrets: _showSecrets,
  setShowSecrets: _setShowSecrets,
  testResults,
  onConfigChange,
  onSave,
  onRefresh,
  onTestConnection,
  onTestAI,
  onGenerateToken: _onGenerateToken,
  onPullModel: _onPullModel,
}: {
  config: WhatsAppConfigForm;
  status: WhatsAppStatus | null;
  models: string[];
  loading: boolean;
  showSecrets: { accessToken: boolean; appSecret: boolean };
  setShowSecrets: (value: { accessToken: boolean; appSecret: boolean }) => void;
  testResults: { connection?: string; ai?: string };
  onConfigChange: (value: WhatsAppConfigForm | ((prev: WhatsAppConfigForm) => WhatsAppConfigForm)) => void;
  onSave: (partial?: Partial<WhatsAppConfigForm>) => Promise<void>;
  onRefresh: () => Promise<void>;
  onTestConnection: () => Promise<void>;
  onTestAI: () => Promise<void>;
  onGenerateToken: () => Promise<void>;
  onPullModel: (modelName: string) => Promise<void>;
}) {
  const [qrPreview, setQrPreview] = useState<string | null>(null);
  const [openwaLogText, setOpenwaLogText] = useState<string>('');
  const [digestOperators, setDigestOperators] = useState<
    Array<{
      id: string;
      firstName: string;
      lastName: string;
      role: string;
      isActive: boolean;
      whatsappNumber?: string | null;
      whatsappAgendaDigest?: boolean;
    }>
  >([]);
  const [digestRunning, setDigestRunning] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    void fetch('/api/users', { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.data)) {
          setDigestOperators(data.data);
        }
      })
      .catch(() => undefined);
  }, []);

  async function saveDigestSettings() {
    await onSave({
      morningDigestEnabled: config.morningDigestEnabled,
      morningDigestHour: config.morningDigestHour,
    });
    const token = localStorage.getItem('accessToken');
    await Promise.all(
      digestOperators.map((op) =>
        fetch(`/api/users/${op.id}/whatsapp-agenda-digest`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            whatsappAgendaDigest: Boolean(op.whatsappAgendaDigest && op.whatsappNumber),
          }),
        })
      )
    );
  }

  async function runDigestNow() {
    setDigestRunning(true);
    try {
      await saveDigestSettings();
      const token = localStorage.getItem('accessToken');
      const res = await fetch('/api/whatsapp/morning-digest/run', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.success) {
        toast.error(data.message || 'Invio digest non riuscito');
        return;
      }
      const sent = data.data?.sent ?? 0;
      const skippedAlready = data.data?.skippedAlready ?? 0;
      const skippedNoAppointments = data.data?.skippedNoAppointments ?? 0;
      toast.success(
        `Digest: ${sent} inviati, ${skippedAlready} già ricevuti oggi, ${skippedNoAppointments} senza appuntamenti`
      );
    } catch (error: any) {
      toast.error(error?.message || 'Errore invio digest');
    } finally {
      setDigestRunning(false);
    }
  }

  return (
    <Card>
      <CardHeader className="space-y-2 lg:flex lg:items-center lg:justify-between lg:space-y-0">
        <div>
          <CardTitle>Configurazione WhatsApp (OpenWA)</CardTitle>
          <CardDescription>
            Sessione OpenWA, risposte AI e digest agenda mattutino per gli operatori.
          </CardDescription>
            </div>
        <Button variant="outline" onClick={onRefresh} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Aggiorna stato
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-lg border p-4 space-y-3 bg-muted/30">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="font-semibold">Transport & OpenWA</h3>
              <p className="text-sm text-muted-foreground">
                Un solo canale attivo. Engine (read-only): {config.engine || status?.engine || 'whatsapp-web.js'}
              </p>
            </div>
            <div className="text-right">
              <div className="border rounded px-3 py-1 text-sm bg-muted font-medium">OPENWA</div>
              <p className="text-xs text-muted-foreground mt-1">Canale fisso (non modificabile da qui)</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              onClick={async () => {
                try {
                  await onSave({
                    openwaBaseUrl: config.openwaBaseUrl,
                    openwaApiKey: config.openwaApiKey || undefined,
                  } as any);
                } catch (error: any) {
                  toast.error(error?.message || 'Errore salvataggio transport');
                }
              }}
            >
              Salva transport
            </Button>            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={async () => {
                const token = localStorage.getItem('accessToken');
                const res = await fetch('/api/whatsapp/openwa/bootstrap', {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                if (data.success) {
                  toast.success('OpenWA inizializzato');
                  await onRefresh();
                } else toast.error(data.message || 'Bootstrap fallito');
              }}
            >
              Inizializza OpenWA
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={async () => {
                const token = localStorage.getItem('accessToken');
                await fetch('/api/whatsapp/openwa/session/start', {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` },
                });
                await onRefresh();
              }}
            >
              Start sessione
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={async () => {
                const token = localStorage.getItem('accessToken');
                const res = await fetch('/api/whatsapp/openwa/qr', {
                  headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                if (!data.success) {
                  toast.error(data.message || 'QR non disponibile');
                  return;
                }
                const payload = data.data || {};
                const url =
                  payload.dataUrl ||
                  payload.qrCode ||
                  payload.qr ||
                  (typeof payload === 'string' ? payload : null);
                if (url && String(url).length > 20) {
                  setQrPreview(String(url).startsWith('data:') ? String(url) : `data:image/png;base64,${url}`);
                  toast.success('QR aggiornato');
                } else {
                  setQrPreview(null);
                  toast.error(payload.status || 'QR non pronto — avvia la sessione e riprova');
                }
                await onRefresh();
              }}
            >
              Mostra QR
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={async () => {
                if (!confirm('Riavviare il container OpenWA?')) return;
                const token = localStorage.getItem('accessToken');
                const res = await fetch('/api/whatsapp/openwa/restart', {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                if (data.success) toast.success('Restart richiesto');
                else toast.error(data.message || 'Restart fallito');
              }}
            >
              Restart OpenWA
            </Button>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              onClick={async () => {
                if (!confirm('Logout + wipe sessione OpenWA (agent host)?')) return;
                const token = localStorage.getItem('accessToken');
                await fetch('/api/whatsapp/openwa/session/logout', {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` },
                });
                const res = await fetch('/api/whatsapp/openwa/wipe', {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                if (data.success) toast.success('Wipe completato — riesegui bootstrap/QR');
                else toast.error(data.message || 'Wipe fallito');
                setQrPreview(null);
                await onRefresh();
              }}
            >
              Wizard wipe numero
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={async () => {
                const token = localStorage.getItem('accessToken');
                const res = await fetch('/api/whatsapp/openwa/logs?lines=120', {
                  headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                if (data.success) {
                  setOpenwaLogText(String(data.data?.text || ''));
                  toast.success('Log caricati');
                } else toast.error(data.message || 'Log non disponibili');
              }}
            >
              Log OpenWA
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={async () => {
                const token = localStorage.getItem('accessToken');
                toast.info('Sync inbox in corso (chat + messaggi)…');
                const res = await fetch('/api/whatsapp/openwa/chats/sync', {
                  method: 'POST',
                  headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({ maxChats: 100, messageLimit: 30 }),
                });
                const data = await res.json();
                if (data.success) {
                  const d = data.data || {};
                  toast.success(
                    `Sync OK: ${d.direct || 0} 1:1, ${d.groups || 0} gruppi, ${d.messagesUpserted || 0} msg`
                  );
                } else toast.error(data.message || 'Sync inbox fallita');
              }}
            >
              Sync inbox (1:1 + gruppi)
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={async () => {
                const token = localStorage.getItem('accessToken');
                const res = await fetch('/api/whatsapp/openwa/webhook/register', {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                if (data.success) toast.success('Webhook OpenWA registrato');
                else toast.error(data.message || 'Registrazione webhook fallita');
              }}
            >
              Registra webhook
            </Button>
          </div>
          {qrPreview ? (
            <div className="flex flex-col items-start gap-2">
              <p className="text-xs text-muted-foreground">Scansiona con WhatsApp sul telefono</p>
              <img src={qrPreview} alt="OpenWA QR" className="h-56 w-56 rounded border bg-white p-2" />
            </div>
          ) : null}
          {openwaLogText ? (
            <pre className="max-h-48 overflow-auto rounded border bg-black/90 p-2 text-[10px] text-green-300 whitespace-pre-wrap">
              {openwaLogText}
            </pre>
          ) : null}
          <p className="text-xs text-muted-foreground">
            Sessione: {config.openwaSessionId || '—'} · Status: {status?.session?.status || status?.transport || '—'} ·
            Webhook OpenWA: {status?.openwaWebhookUrl || '—'}
          </p>
          <input
            className="w-full border rounded px-2 py-1 text-sm"
            placeholder="OpenWA API key (solo se vuoi sovrascrivere)"
            value={config.openwaApiKey}
            onChange={(e) => onConfigChange({ ...config, openwaApiKey: e.target.value })}
          />
        </div>

        <div className="rounded-lg border p-4 space-y-4">
          <div>
            <h3 className="font-semibold">Digest agenda mattutino</h3>
            <p className="text-sm text-muted-foreground">
              Ogni giorno all&apos;ora indicata (Europe/Rome) ogni operatore con flag attivo riceve i propri
              appuntamenti su WhatsApp. Senza appuntamenti non parte nessun messaggio. Dopo aver cambiato
              l&apos;ora, premi Salva digest: l&apos;invio automatico usa la configurazione OpenWA attiva.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-3">
              <Switch
                checked={config.morningDigestEnabled}
                onCheckedChange={(checked) =>
                  onConfigChange({ ...config, morningDigestEnabled: checked })
                }
              />
              <Label>Attivo per tutti</Label>
            </div>
            <div className="space-y-1">
              <Label htmlFor="morning-digest-hour">Ora invio</Label>
              <Input
                id="morning-digest-hour"
                type="time"
                className="w-32"
                value={config.morningDigestHour}
                onChange={(e) =>
                  onConfigChange({ ...config, morningDigestHour: e.target.value })
                }
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Operatori</Label>
            <div className="max-h-56 overflow-auto rounded border divide-y">
              {digestOperators.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">Nessun utente caricato.</p>
              ) : (
                digestOperators.map((op) => {
                  const hasNumber = Boolean(String(op.whatsappNumber || '').trim());
                  const label = `${op.firstName || ''} ${op.lastName || ''}`.trim() || op.id;
                  return (
                    <label
                      key={op.id}
                      className={`flex items-center justify-between gap-3 px-3 py-2 text-sm ${
                        hasNumber ? '' : 'opacity-60'
                      }`}
                    >
                      <span>
                        {label}
                        <span className="block text-xs text-muted-foreground">
                          {hasNumber ? op.whatsappNumber : 'Manca numero WhatsApp — flag bloccato'}
                        </span>
                      </span>
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        disabled={!hasNumber}
                        checked={Boolean(hasNumber && op.whatsappAgendaDigest)}
                        onChange={(e) =>
                          setDigestOperators((prev) =>
                            prev.map((row) =>
                              row.id === op.id
                                ? { ...row, whatsappAgendaDigest: e.target.checked }
                                : row
                            )
                          )
                        }
                      />
                    </label>
                  );
                })
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" disabled={loading || digestRunning} onClick={() => void saveDigestSettings()}>
              <Save className="h-4 w-4 mr-2" /> Salva digest
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={loading || digestRunning || !config.morningDigestEnabled}
              onClick={() => void runDigestNow()}
            >
              {digestRunning ? 'Invio…' : 'Invia ora'}
            </Button>
          </div>
        </div>

        {status && (
          <div className="grid gap-4 md:grid-cols-4">
            <StatusTile
              title="Configurazione"
              status={status.configured ? 'Pronta' : 'Da completare'}
              description={status.configured ? `Transport ${status.transport || 'OPENWA'}` : 'Completa la sessione OpenWA'}
            />
            <StatusTile
              title="AI"
              status={status.aiEnabled ? `Attiva (${status.model})` : 'Spenta'}
              description={status.aiEnabled ? 'L AI risponde alle chat' : 'Risposte manuali'}
            />
            <StatusTile
              title="Auto Reply"
              status={status.autoReply ? 'Attiva' : 'Manuale'}
              description={status.autoReply ? 'Le richieste ricevono risposta' : 'Solo operatori'}
            />
            <StatusTile
              title="Webhook"
              status={status.webhookToken ? 'Configurato' : 'Assente'}
              description="Token webhook OpenWA"
            />
              </div>
        )}

        <Tabs defaultValue="ai" className="w-full">
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="ai" className="flex items-center gap-2">
              <Bot className="h-4 w-4" /> AI & Modelli
            </TabsTrigger>
            <TabsTrigger value="status" className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" /> Test & Monitor
            </TabsTrigger>
          </TabsList>

          <TabsContent value="ai" className="pt-6 space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center gap-2">
                <Bot className="h-5 w-5 text-blue-600" />
                <div>
                  <h4 className="font-medium text-blue-900">Configurazione AI Centralizzata</h4>
                  <p className="text-sm text-blue-800">
                    I modelli AI, prompt e configurazioni avanzate sono ora gestiti nella sezione <strong>AI Core</strong>.
                    Le impostazioni qui sono specifiche per WhatsApp Business.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between border rounded-lg p-4">
              <div className="flex flex-col gap-1">
                <Label className="flex items-center gap-2">
                  <Bot className="h-4 w-4" /> Abilita risposte automatiche AI per WhatsApp
                </Label>
                <p className="text-xs text-muted-foreground">
                  L'AI risponderà automaticamente ai messaggi WhatsApp utilizzando le impostazioni globali.
                </p>
              </div>
              <Switch
                checked={config.autoReply}
                onCheckedChange={(checked) => onConfigChange({ ...config, autoReply: checked, aiEnabled: checked })}
              />
            </div>

            <div className="space-y-2">
              <Label>Messaggio di presentazione (primo contatto)</Label>
              <p className="text-xs text-muted-foreground">
                Viene inviato al primo messaggio dello studio verso un <strong>cliente</strong> (preventivo,
                fattura o risposta AI). Non viene mai inviato agli operatori. Serve a dichiarare che la chat è
                gestita da un sistema di intelligenza artificiale (obbligo di trasparenza, art. 50 AI Act).
                Lascia vuoto per non inviarlo. Per le email il testo è distinto e si modifica in Impostazioni →
                Email: viene aggiunto in calce a ogni messaggio in uscita.
              </p>
              <Textarea
                rows={5}
                value={config.aiDisclosureMessage}
                onChange={(e) => onConfigChange({ ...config, aiDisclosureMessage: e.target.value })}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button disabled={loading} onClick={() => onSave()}>
                <Save className="h-4 w-4 mr-2" /> Salva impostazioni WhatsApp AI
              </Button>
              <Button variant="outline" disabled={loading} onClick={onTestAI}>
                <TestTube className="h-4 w-4 mr-2" /> Test AI
              </Button>
            </div>
            {testResults.ai && (
              <div className="rounded-md bg-green-50 p-3 text-sm text-green-800">
                {testResults.ai}
              </div>
            )}
          </TabsContent>

          <TabsContent value="status" className="pt-6 space-y-4">
            <Card className="bg-muted">
              <CardHeader>
                <CardTitle>Monitoraggio</CardTitle>
                <CardDescription>Test rapidi e stato operativo.</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={onTestConnection} disabled={loading}>
                  <TestTube className="h-4 w-4 mr-2" /> Test connessione
                </Button>
                <Button variant="outline" onClick={onTestAI} disabled={loading}>
                  <Bot className="h-4 w-4 mr-2" /> Test AI
                </Button>
                <Button variant="outline" onClick={onRefresh} disabled={loading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Aggiorna stato
                </Button>
              </CardContent>
          </Card>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

function EmailSettingsSection({
  settings,
  savingKey,
  testingKey,
  onSave,
  onTest,
}: {
  settings: BaseSettings;
  savingKey: string | null;
  testingKey: string | null;
  onSave: (payload: BaseSettings['email']) => Promise<void>;
  onTest: () => Promise<void>;
}) {
  const [form, setForm] = useState(settings.email);

  useEffect(() => {
    setForm(settings.email);
  }, [settings.email]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Configurazione Email Sistema</CardTitle>
        <CardDescription>
          Account email centralizzato dello studio. Tutti gli utenti vedono le stesse email in arrivo e inviate.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Provider email</Label>
          <Select
            value={form.provider}
            onValueChange={(value) => setForm((prev) => ({ ...prev, provider: value as any }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="local">🏠 Server Mail Locale (Studio Gori)</SelectItem>
              <SelectItem value="gmail">Gmail</SelectItem>
              <SelectItem value="outlook">Outlook</SelectItem>
              <SelectItem value="custom">Custom SMTP</SelectItem>
            </SelectContent>
          </Select>
            </div>

        {form.provider === 'local' && (
          <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4">
            <p className="text-sm font-medium mb-3 text-blue-900">
              ℹ️ Server Mail Locale - Account Sistema Centralizzato
            </p>
            <p className="text-xs text-muted-foreground mb-3">
              Studio Gori usa un unico account email condiviso per tutto lo studio. Tutti gli utenti (Admin e Operatori) vedono le stesse email in arrivo e inviate.
            </p>
            <div className="space-y-2">
              <div className="flex justify-between items-center bg-white/50 p-3 rounded border-2 border-blue-300">
                <div>
                  <span className="font-mono font-medium text-base">pierinoai@vps-3dee2600.vps.ovh.net</span>
                  <p className="text-xs text-muted-foreground mt-1">Account email sistema (condiviso)</p>
                </div>
                <Button 
                  variant="default" 
                  size="sm"
                  onClick={() => setForm(prev => ({ 
                    ...prev, 
                    username: 'pierinoai@vps-3dee2600.vps.ovh.net',
                    host: 'mailserver',
                    port: 587, 
                    secure: false
                  }))}
                >
                  Usa questo
                </Button>
              </div>
            </div>
            <div className="mt-3 space-y-1">
              <p className="text-xs text-blue-600">
                🔧 <strong>Host</strong>: mailserver (interno Docker)
              </p>
              <p className="text-xs text-blue-600">
                🔧 <strong>Porta SMTP</strong>: 587 (STARTTLS) - <strong>Porta IMAP</strong>: 993 (SSL)
              </p>
              <p className="text-xs text-blue-600">
                🔒 <strong>Sicurezza</strong>: TLS moderno (TLSv1.2+)
              </p>
            </div>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Username / Email</Label>
                <Input
              type="email"
              value={form.username || ''}
              onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
                />
              </div>
          <div className="space-y-2">
            <Label>Password / App password</Label>
                <Input
              type="password"
              value={form.password || ''}
              onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                />
              </div>
              </div>
        {form.provider === 'custom' && (
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Host SMTP</Label>
              <Input
                value={form.host || ''}
                onChange={(e) => setForm((prev) => ({ ...prev, host: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Porta</Label>
              <Input
                type="number"
                value={form.port || 587}
                onChange={(e) => setForm((prev) => ({ ...prev, port: parseInt(e.target.value || '0', 10) }))}
              />
            </div>
            <div className="flex items-center gap-2 mt-6">
              <Switch
                checked={form.secure}
                onCheckedChange={(checked) => setForm((prev) => ({ ...prev, secure: checked }))}
              />
              <span className="text-sm text-muted-foreground">Usa SSL/TLS</span>
        </div>
      </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => onSave(form)} disabled={savingKey === 'email'}>
            <Save className="h-4 w-4 mr-2" /> {savingKey === 'email' ? 'Salvando...' : 'Salva email'}
          </Button>
          <Button variant="outline" onClick={onTest} disabled={savingKey === 'email:test'}>
            <TestTube className="h-4 w-4 mr-2" /> {savingKey === 'email:test' ? 'Test in corso...' : 'Test connessione'}
          </Button>
    </div>
      </CardContent>
    </Card>
  );
}

function PlaceholderCard({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <Card className="border-dashed">
      <CardHeader className="flex flex-row items-center gap-4">
        <div className="rounded-full bg-muted p-3 text-muted-foreground">{icon}</div>
        <div>
          <CardTitle>{title}</CardTitle>
          <CardDescription>Configurazioni dedicate verranno aggiunte in una release successiva.</CardDescription>
        </div>
      </CardHeader>
    </Card>
  );
}

// AI Timeouts Section Component
function AITimeoutsSection({
  settings,
  savingKey,
  onSave,
}: {
  settings: BaseSettings;
  savingKey: string | null;
  onSave: (payload: BaseSettings['aiTimeouts']) => Promise<void>;
}) {
  const [form, setForm] = useState(settings.aiTimeouts);

  useEffect(() => {
    setForm(settings.aiTimeouts);
  }, [settings.aiTimeouts]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Timeout AI per Servizio
        </CardTitle>
        <CardDescription>
          Configura timeout specifici per ogni servizio AI. Tempi più lunghi per operazioni complesse.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-2">
            <Label>WhatsApp AI (secondi)</Label>
            <Input
              type="number"
              min="5"
              max="300"
              value={Math.round(form.whatsapp / 1000)}
              onChange={(e) => setForm((prev) => ({ ...prev, whatsapp: (parseInt(e.target.value) || 60) * 1000 }))}
              placeholder="60"
            />
            <p className="text-xs text-muted-foreground">Timeout per risposte AI WhatsApp</p>
          </div>
          
          <div className="space-y-2">
            <Label>Email AI (secondi)</Label>
            <Input
              type="number"
              min="5"
              max="300"
              value={Math.round(form.email / 1000)}
              onChange={(e) => setForm((prev) => ({ ...prev, email: (parseInt(e.target.value) || 45) * 1000 }))}
              placeholder="45"
            />
            <p className="text-xs text-muted-foreground">Timeout per gestione AI email</p>
          </div>
          
          <div className="space-y-2">
            <Label>Ricerca Documenti (secondi)</Label>
            <Input
              type="number"
              min="5"
              max="300"
              value={Math.round(form.documents / 1000)}
              onChange={(e) => setForm((prev) => ({ ...prev, documents: (parseInt(e.target.value) || 90) * 1000 }))}
              placeholder="90"
            />
            <p className="text-xs text-muted-foreground">Timeout per ricerca AI documenti</p>
          </div>
          
          <div className="space-y-2">
            <Label>Chat Generale (secondi)</Label>
            <Input
              type="number"
              min="5"
              max="300"
              value={Math.round(form.general / 1000)}
              onChange={(e) => setForm((prev) => ({ ...prev, general: (parseInt(e.target.value) || 30) * 1000 }))}
              placeholder="30"
            />
            <p className="text-xs text-muted-foreground">Timeout per chat AI generale</p>
          </div>
          
          <div className="space-y-2">
            <Label>Calendario AI (secondi)</Label>
            <Input
              type="number"
              min="5"
              max="300"
              value={Math.round(form.calendar / 1000)}
              onChange={(e) => setForm((prev) => ({ ...prev, calendar: (parseInt(e.target.value) || 30) * 1000 }))}
              placeholder="30"
            />
            <p className="text-xs text-muted-foreground">Timeout per operazioni AI calendario</p>
          </div>
          
          <div className="space-y-2">
            <Label>Gestione Pratiche (secondi)</Label>
            <Input
              type="number"
              min="5"
              max="300"
              value={Math.round(form.practices / 1000)}
              onChange={(e) => setForm((prev) => ({ ...prev, practices: (parseInt(e.target.value) || 60) * 1000 }))}
              placeholder="60"
            />
            <p className="text-xs text-muted-foreground">Timeout per AI gestione pratiche</p>
          </div>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">💡 Linee Guida Timeout</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li><strong>Chat Generale:</strong> 30s - Risposte rapide</li>
            <li><strong>WhatsApp/Pratiche:</strong> 60s - Analisi contesto e tools</li>
            <li><strong>Ricerca Documenti:</strong> 90s - OCR e ricerca contenuti</li>
            <li><strong>Email:</strong> 45s - Elaborazione allegati</li>
          </ul>
        </div>

        <Button onClick={() => onSave(form)} disabled={savingKey === 'ai-timeouts'} className="w-full">
          <Save className="h-4 w-4 mr-2" />
          {savingKey === 'ai-timeouts' ? 'Salvando...' : 'Salva Timeout AI'}
        </Button>
      </CardContent>
    </Card>
  );
}

// Backup Schedules Section Component
function BackupSchedulesSection() {
  const [schedules, setSchedules] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<any | null>(null);
  const [showForm, setShowForm] = useState(false);

  // Preset per schedule user-friendly
  const schedulePresets = [
    { value: 'daily-2am', label: 'Giornaliero alle 2:00', cron: '0 2 * * *' },
    { value: 'daily-3am', label: 'Giornaliero alle 3:00', cron: '0 3 * * *' },
    { value: 'daily-6am', label: 'Giornaliero alle 6:00', cron: '0 6 * * *' },
    { value: 'weekly-monday-2am', label: 'Settimanale (Lunedì alle 2:00)', cron: '0 2 * * 1' },
    { value: 'weekly-sunday-2am', label: 'Settimanale (Domenica alle 2:00)', cron: '0 2 * * 0' },
    { value: 'monthly-1st-2am', label: 'Mensile (1° del mese alle 2:00)', cron: '0 2 1 * *' },
    { value: 'custom', label: 'Personalizzato', cron: '' },
  ];

  interface BackupSchedule {
    id: string;
    name: string;
    schedule: string;
    type: 'FULL' | 'SCHEMA';
    schema?: string;
    compress: boolean;
    encrypt: boolean;
    enabled: boolean;
    retentionDays: number;
    createdAt: string;
    updatedAt: string;
  }

  const withToken = useMemo(
    () => (init?: RequestInit) => {
      const token = localStorage.getItem('accessToken');
      return {
        ...init,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(init?.headers || {}),
        },
      } satisfies RequestInit;
    },
    []
  );

  useEffect(() => {
    loadSchedules();
  }, []);

  async function loadSchedules() {
    try {
      setLoading(true);
      const response = await fetch('/api/backup/schedules', withToken());
      const data = await response.json();
      if (data.success) {
        setSchedules(data.data || []);
      }
    } catch (error) {
      console.error('Error loading schedules:', error);
      toast.error('Errore nel caricamento degli schedules');
    } finally {
      setLoading(false);
    }
  }

  async function saveSchedule(scheduleData: Partial<BackupSchedule>) {
    try {
      console.log('🔧 [BACKUP-SCHEDULES] Saving schedule:', scheduleData);
      setLoading(true);
      const url = editingSchedule ? `/api/backup/schedules/${editingSchedule.id}` : '/api/backup/schedules';
      const method = editingSchedule ? 'PUT' : 'POST';
      
      console.log('🔧 [BACKUP-SCHEDULES] Making request to:', url, 'with method:', method);
      
      const response = await fetch(url, withToken({
        method,
        body: JSON.stringify(scheduleData),
      }));
      
      console.log('🔧 [BACKUP-SCHEDULES] Response status:', response.status);
      const data = await response.json();
      console.log('🔧 [BACKUP-SCHEDULES] Response data:', data);
      
      if (data.success) {
        toast.success(editingSchedule ? 'Schedule aggiornato!' : 'Schedule creato!');
        setShowForm(false);
        setEditingSchedule(null);
        await loadSchedules();
      } else {
        toast.error(data.message || 'Errore nel salvataggio');
      }
    } catch (error) {
      console.error('❌ [BACKUP-SCHEDULES] Error saving schedule:', error);
      toast.error('Errore nel salvataggio dello schedule');
    } finally {
      setLoading(false);
    }
  }

  async function deleteSchedule(id: string) {
    try {
      setLoading(true);
      const response = await fetch(`/api/backup/schedules/${id}`, withToken({
        method: 'DELETE',
      }));
      
      const data = await response.json();
      if (data.success) {
        toast.success('Schedule eliminato!');
        await loadSchedules();
      } else {
        toast.error(data.message || 'Errore nell\'eliminazione');
      }
    } catch (error) {
      console.error('Error deleting schedule:', error);
      toast.error('Errore nell\'eliminazione dello schedule');
    } finally {
      setLoading(false);
    }
  }

  async function executeSchedule(id: string) {
    try {
      setLoading(true);
      const response = await fetch(`/api/backup/schedules/${id}/execute`, withToken({
        method: 'POST',
      }));
      
      const data = await response.json();
      if (data.success) {
        toast.success('Backup eseguito con successo!');
      } else {
        toast.error(data.message || 'Errore nell\'esecuzione del backup');
      }
    } catch (error) {
      console.error('Error executing schedule:', error);
      toast.error('Errore nell\'esecuzione del backup');
    } finally {
      setLoading(false);
    }
  }

  function toggleSchedule(schedule: BackupSchedule) {
    const updatedSchedule = { ...schedule, enabled: !schedule.enabled };
    saveSchedule(updatedSchedule);
  }

  function startEditing(schedule: BackupSchedule) {
    setEditingSchedule(schedule);
    setShowForm(true);
  }

  function startCreating() {
    setEditingSchedule(null);
    setShowForm(true);
  }

  // Converte espressione cron in descrizione user-friendly
  function getScheduleDescription(cronExpression: string): string {
    const preset = schedulePresets.find(p => p.cron === cronExpression);
    if (preset) {
      return preset.label;
    }
    // Fallback per espressioni personalizzate
    return `Cron: ${cronExpression}`;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Backup Automatici
          </CardTitle>
          <CardDescription>
            Gestisci gli schedule di backup automatici del database con retention policy e opzioni di compressione/cifratura.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Button onClick={startCreating} disabled={loading}>
              <Database className="h-4 w-4 mr-2" />
              Nuovo Schedule
            </Button>
            <Button variant="outline" onClick={loadSchedules} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Aggiorna
            </Button>
          </div>

          {schedules.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessuno schedule configurato</p>
              <p className="text-sm">Crea il primo schedule di backup automatico</p>
            </div>
          ) : (
            <div className="space-y-4">
              {schedules.map((schedule) => (
                <Card key={schedule.id} className="border">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{schedule.name}</h4>
                          <span className={`px-2 py-1 rounded text-xs ${
                            schedule.enabled 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {schedule.enabled ? '🟢 Abilitato' : '🔴 Disabilitato'}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Tipo:</span>
                            <p className="font-medium">
                              {schedule.type} 
                              {schedule.type === 'SCHEMA' && schedule.schema && ` (${schedule.schema})`}
                            </p>
                          </div>
                              <div>
                                <span className="text-muted-foreground">Frequenza:</span>
                                <p className="text-sm">
                                  {getScheduleDescription(schedule.schedule)}
                                </p>
                              </div>
                          <div>
                            <span className="text-muted-foreground">Retention:</span>
                            <p className="font-medium">{schedule.retentionDays} giorni</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Storage:</span>
                            <p className="text-sm">
                              {schedule.storageType === 'local' && '📁 Locale'}
                              {schedule.storageType === 'ftp' && `🌐 FTP (${schedule.ftpHost || 'N/A'})`}
                              {schedule.storageType === 'scp' && `🔐 SCP (${schedule.scpHost || 'N/A'})`}
                            </p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Opzioni:</span>
                            <p className="text-xs">
                              {schedule.compress && '✅ Compress '}
                              {schedule.encrypt && '🔒 Encrypt'}
                              {!schedule.compress && !schedule.encrypt && 'Nessuna'}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => startEditing(schedule)}
                          disabled={loading}
                        >
                          Modifica
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => executeSchedule(schedule.id)}
                          disabled={loading}
                        >
                          🔄 Esegui Ora
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleSchedule(schedule)}
                          disabled={loading}
                        >
                          {schedule.enabled ? '⏹ Disabilita' : '▶ Abilita'}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deleteSchedule(schedule.id)}
                          disabled={loading}
                        >
                          Elimina
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Modal */}
      {showForm && (
        <BackupScheduleForm
          schedule={editingSchedule}
          schedulePresets={schedulePresets}
          onSave={saveSchedule}
          onCancel={() => {
            setShowForm(false);
            setEditingSchedule(null);
          }}
          loading={loading}
        />
      )}
    </div>
  );
}

// Backup Schedule Form Component
function BackupScheduleForm({ 
  schedule, 
  schedulePresets,
  onSave, 
  onCancel, 
  loading 
}: { 
  schedule: any | null; 
  schedulePresets: Array<{value: string; label: string; cron: string}>;
  onSave: (data: any) => void; 
  onCancel: () => void; 
  loading: boolean; 
}) {
  const [form, setForm] = useState({
    name: '',
    schedule: '0 2 * * *',
    schedulePreset: 'daily-2am',
    type: 'FULL' as 'FULL' | 'SCHEMA',
    schema: '',
    compress: true,
    encrypt: false,
    enabled: true,
    retentionDays: 30,
    // Storage configuration
    storageType: 'local' as 'local' | 'ftp' | 'scp',
    storagePath: '',
    ftpHost: '',
    ftpPort: 21,
    ftpUser: '',
    ftpPass: '',
    ftpDir: '',
    scpHost: '',
    scpPort: 22,
    scpUser: '',
    scpKeyPath: '',
    scpDir: '',
  });

  useEffect(() => {
    if (schedule) {
      // Trova il preset corrispondente o imposta custom
      const matchingPreset = schedulePresets.find(preset => preset.cron === schedule.schedule);
      setForm({
        name: schedule.name || '',
        schedule: schedule.schedule || '0 2 * * *',
        schedulePreset: matchingPreset ? matchingPreset.value : 'custom',
        type: schedule.type || 'FULL',
        schema: schedule.schema || '',
        compress: schedule.compress ?? true,
        encrypt: schedule.encrypt ?? false,
        enabled: schedule.enabled ?? true,
        retentionDays: schedule.retentionDays || 30,
        // Storage configuration
        storageType: schedule.storageType || 'local',
        storagePath: schedule.storagePath || '',
        ftpHost: schedule.ftpHost || '',
        ftpPort: schedule.ftpPort || 21,
        ftpUser: schedule.ftpUser || '',
        ftpPass: schedule.ftpPass || '',
        ftpDir: schedule.ftpDir || '',
        scpHost: schedule.scpHost || '',
        scpPort: schedule.scpPort || 22,
        scpUser: schedule.scpUser || '',
        scpKeyPath: schedule.scpKeyPath || '',
        scpDir: schedule.scpDir || '',
      });
    }
  }, [schedule]);

  // Gestisce il cambio di preset
  function handleSchedulePresetChange(presetValue: string) {
    const preset = schedulePresets.find(p => p.value === presetValue);
    if (preset && preset.cron) {
      setForm(prev => ({
        ...prev,
        schedulePreset: presetValue,
        schedule: preset.cron
      }));
    } else {
      setForm(prev => ({
        ...prev,
        schedulePreset: presetValue
      }));
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave(form);
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>
            {schedule ? 'Modifica Schedule' : 'Nuovo Schedule'}
          </CardTitle>
          <CardDescription>
            Configura uno schedule di backup automatico con espressione cron.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Nome Schedule</Label>
                        <Input
                          value={form.name}
                          onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Backup Giornaliero"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Frequenza Backup</Label>
                        <Select
                          value={form.schedulePreset}
                          onValueChange={handleSchedulePresetChange}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {schedulePresets.map((preset) => (
                              <SelectItem key={preset.value} value={preset.value}>
                                {preset.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {form.schedulePreset === 'custom' && (
                      <div className="space-y-2">
                        <Label>Espressione Cron Personalizzata</Label>
                        <Input
                          value={form.schedule}
                          onChange={(e) => setForm(prev => ({ ...prev, schedule: e.target.value }))}
                          placeholder="0 2 * * *"
                          required
                        />
                        <p className="text-xs text-muted-foreground">
                          Formato: minuto ora giorno mese giorno_settimana (es: 0 2 * * * = ogni giorno alle 2:00)
                        </p>
                      </div>
                    )}

                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Prossimo backup:</strong> {form.schedulePreset !== 'custom' ? 
                        schedulePresets.find(p => p.value === form.schedulePreset)?.label : 
                        `Cron: ${form.schedule}`
                      }
                    </p>
                  </div>

                  {/* Storage Configuration */}
                  <div className="space-y-4">
                    <div>
                      <Label>Tipo Storage</Label>
                      <Select
                        value={form.storageType}
                        onValueChange={(value: 'local' | 'ftp' | 'scp') => 
                          setForm(prev => ({ ...prev, storageType: value }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="local">Locale (Server)</SelectItem>
                          <SelectItem value="ftp">FTP Remoto</SelectItem>
                          <SelectItem value="scp">SCP/SFTP</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {form.storageType === 'local' && (
                      <div className="space-y-2">
                        <Label>Percorso Locale</Label>
                        <Input
                          value={form.storagePath}
                          onChange={(e) => setForm(prev => ({ ...prev, storagePath: e.target.value }))}
                          placeholder="/var/backups/studio-gori"
                        />
                        <p className="text-xs text-muted-foreground">
                          Percorso sul server dove salvare i backup (default: /app/data/backups)
                        </p>
                      </div>
                    )}

                    {form.storageType === 'ftp' && (
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Host FTP</Label>
                          <Input
                            value={form.ftpHost}
                            onChange={(e) => setForm(prev => ({ ...prev, ftpHost: e.target.value }))}
                            placeholder="ftp.example.com"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Porta</Label>
                          <Input
                            type="number"
                            value={form.ftpPort}
                            onChange={(e) => setForm(prev => ({ ...prev, ftpPort: parseInt(e.target.value) || 21 }))}
                            placeholder="21"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Username</Label>
                          <Input
                            value={form.ftpUser}
                            onChange={(e) => setForm(prev => ({ ...prev, ftpUser: e.target.value }))}
                            placeholder="ftpuser"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Password</Label>
                          <Input
                            type="password"
                            value={form.ftpPass}
                            onChange={(e) => setForm(prev => ({ ...prev, ftpPass: e.target.value }))}
                            placeholder="password"
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label>Directory Remota</Label>
                          <Input
                            value={form.ftpDir}
                            onChange={(e) => setForm(prev => ({ ...prev, ftpDir: e.target.value }))}
                            placeholder="/backups/studio-gori"
                          />
                        </div>
                      </div>
                    )}

                    {form.storageType === 'scp' && (
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Host SCP</Label>
                          <Input
                            value={form.scpHost}
                            onChange={(e) => setForm(prev => ({ ...prev, scpHost: e.target.value }))}
                            placeholder="server.example.com"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Porta</Label>
                          <Input
                            type="number"
                            value={form.scpPort}
                            onChange={(e) => setForm(prev => ({ ...prev, scpPort: parseInt(e.target.value) || 22 }))}
                            placeholder="22"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Username</Label>
                          <Input
                            value={form.scpUser}
                            onChange={(e) => setForm(prev => ({ ...prev, scpUser: e.target.value }))}
                            placeholder="sshuser"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Percorso Chiave SSH</Label>
                          <Input
                            value={form.scpKeyPath}
                            onChange={(e) => setForm(prev => ({ ...prev, scpKeyPath: e.target.value }))}
                            placeholder="/app/ssh/id_rsa"
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label>Directory Remota</Label>
                          <Input
                            value={form.scpDir}
                            onChange={(e) => setForm(prev => ({ ...prev, scpDir: e.target.value }))}
                            placeholder="/home/user/backups/studio-gori"
                          />
                        </div>
                      </div>
                    )}
                  </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Tipo Backup</Label>
                <Select
                  value={form.type}
                  onValueChange={(value) => setForm(prev => ({ ...prev, type: value as 'FULL' | 'SCHEMA' }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FULL">FULL - Backup completo database</SelectItem>
                    <SelectItem value="SCHEMA">SCHEMA - Solo struttura tabelle</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.type === 'SCHEMA' && (
                <div className="space-y-2">
                  <Label>Schema Target</Label>
                  <Input
                    value={form.schema}
                    onChange={(e) => setForm(prev => ({ ...prev, schema: e.target.value }))}
                    placeholder="public"
                    required={form.type === 'SCHEMA'}
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Retention (giorni)</Label>
              <Input
                type="number"
                min="1"
                max="365"
                value={form.retentionDays}
                onChange={(e) => setForm(prev => ({ ...prev, retentionDays: parseInt(e.target.value) || 30 }))}
                required
              />
              <p className="text-xs text-muted-foreground">
                Giorni dopo i quali i backup vengono automaticamente eliminati
              </p>
            </div>

            <div className="space-y-4">
              <h4 className="font-medium">Opzioni Backup</h4>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={form.compress}
                    onCheckedChange={(checked) => setForm(prev => ({ ...prev, compress: checked }))}
                  />
                  <Label>Compressione</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={form.encrypt}
                    onCheckedChange={(checked) => setForm(prev => ({ ...prev, encrypt: checked }))}
                  />
                  <Label>Cifratura</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={form.enabled}
                    onCheckedChange={(checked) => setForm(prev => ({ ...prev, enabled: checked }))}
                  />
                  <Label>Abilitato</Label>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="submit" disabled={loading}>
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Salvando...' : 'Salva Schedule'}
              </Button>
              <Button type="button" variant="outline" onClick={onCancel}>
                Annulla
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}